import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCheck, Clock, Download, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { DateField, isoDay } from "@/components/common/DateField";
import { LogTimeDialog } from "@/components/common/LogTimeDialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

const STATUS_STYLE = {
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  recorded: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  rejected: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

const back = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return isoDay(d);
};

const mondayOf = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return isoDay(d);
};

const weekDays = (monday) => Array.from({ length: 7 }, (_, i) => {
  const d = new Date(`${monday}T00:00:00`);
  d.setDate(d.getDate() + i);
  return isoDay(d);
});

export default function TimeSheet() {
  const { can, user } = useAuth();
  const [range, setRange] = useState({ from: back(30), to: isoDay(new Date()) });
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [person, setPerson] = useState("");
  const [week, setWeek] = useState(mondayOf(isoDay(new Date())));
  const [logging, setLogging] = useState(false);
  const [picked, setPicked] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [entries, sum, m] = await Promise.all([
      api.get("/time/entries", { params: { date_from: range.from, date_to: range.to } }),
      api.get("/time/summary", { params: { date_from: range.from, date_to: range.to } }),
      api.get("/time/meta"),
    ]);
    setData(entries.data);
    setSummary(sum.data);
    setMeta(m.data);
  }, [range]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const queue = useMemo(() => (data?.items || []).filter((e) => e.status === "recorded"), [data]);
  const mine = useMemo(() => {
    const days = weekDays(week);
    return (data?.items || []).filter((e) => (!person || e.person_id === person)
      && days.includes(e.date));
  }, [data, person, week]);

  const act = async (key, fn, message) => {
    setBusy(key);
    let ok = false;
    try {
      await fn();
      toast.success(message);
      setPicked([]);
      await load();
      ok = true;
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
    return ok;
  };

  const exportCsv = async () => {
    const res = await api.get("/time/entries.csv",
      { params: { date_from: range.from, date_to: range.to }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `time-entries-${range.from}-to-${range.to}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (!data || !summary) return <Loading label="Loading the timesheet" />;
  const canWrite = can("volunteer", "write");
  const canApprove = can("volunteer", "approve");
  const days = weekDays(week);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Phase 2 · One timesheet for the whole charity"
        title="Time recording"
        description="Volunteer hours, task time and event or hall-booking time all land in the same sheet. The coordinator approves them, and approved hours are what feed analytics and recognition."
        actions={canWrite && (
          <Button data-testid="log-time-button" onClick={() => setLogging(true)}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="mr-1.5 h-4 w-4" /> Log time
          </Button>
        )}
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label className="text-[11px] text-muted-foreground">From</Label>
          <DateField value={range.from} testId="time-from-input" clearable={false}
                     onChange={(v) => setRange({ ...range, from: v })}
                     className="h-8 w-[150px] text-[11.5px]" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] text-muted-foreground">To</Label>
          <DateField value={range.to} testId="time-to-input" clearable={false}
                     onChange={(v) => setRange({ ...range, to: v })}
                     className="h-8 w-[150px] text-[11.5px]" />
        </div>
        <Button size="sm" variant="outline" data-testid="time-export-csv" onClick={exportCsv}
                className="h-8 rounded-full border-border px-3 text-[10.5px]">
          <Download className="mr-1 h-3 w-3" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="kpi-time-hours" label="Hours in this window" value={data.counters.hours}
             accent="violet" sub={`${data.counters.entries} entries`} />
        <Kpi testId="kpi-time-approved" label="Approved" accent="emerald"
             value={data.counters.approved_hours} />
        <Kpi testId="kpi-time-waiting" label="Waiting on approval" accent="amber"
             value={data.counters.recorded_hours} sub={`${queue.length} entries`} />
        <Kpi testId="kpi-time-people" label="People logging time" value={data.counters.people} />
      </div>

      <Tabs defaultValue="week">
        <TabsList data-testid="time-tabs" className="flex-wrap">
          <TabsTrigger value="week" data-testid="tab-my-week">My week</TabsTrigger>
          <TabsTrigger value="queue" data-testid="tab-review-queue">
            Review queue{queue.length ? ` (${queue.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-entries">All entries</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-time-analytics">Totals</TabsTrigger>
        </TabsList>

        <TabsContent value="week" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={person || "all"}
                    onValueChange={(v) => setPerson(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="week-person-select"
                             className="h-8 w-[230px] bg-muted/40 text-[12px]">
                <SelectValue placeholder="Everyone" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Everyone</SelectItem>
                {(meta?.people || []).map((p) => (
                  <SelectItem key={`${p.kind}-${p.id}`} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" data-testid="week-prev"
                    onClick={() => { const d = new Date(`${week}T00:00:00`); d.setDate(d.getDate() - 7); setWeek(isoDay(d)); }}
                    className="h-8 rounded-full border-border px-3 text-[10.5px]">Previous week</Button>
            <Button size="sm" variant="outline" data-testid="week-this"
                    onClick={() => setWeek(mondayOf(isoDay(new Date())))}
                    className="h-8 rounded-full border-border px-3 text-[10.5px]">This week</Button>
            <Button size="sm" variant="outline" data-testid="week-next"
                    onClick={() => { const d = new Date(`${week}T00:00:00`); d.setDate(d.getDate() + 7); setWeek(isoDay(d)); }}
                    className="h-8 rounded-full border-border px-3 text-[10.5px]">Next week</Button>
            <span className="text-[11px] text-muted-foreground" data-testid="week-label">
              week of {shortDate(week)}
            </span>
          </div>

          <Panel testId="panel-my-week" kicker="Seven days" title="Hours logged this week">
            <div className="grid grid-cols-7 gap-px bg-border" data-testid="week-grid">
              {days.map((day) => {
                const rows = mine.filter((e) => e.date === day);
                const total = rows.reduce((sum, e) => sum + e.hours, 0);
                return (
                  <div key={day} className="min-h-[150px] bg-card p-2" data-testid={`week-day-${day}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                        {new Date(`${day}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric" })}
                      </span>
                      <span className="font-mono text-[11px] font-bold text-foreground">
                        {total ? `${total}h` : ""}
                      </span>
                    </div>
                    <div className="mt-1.5 space-y-1">
                      {rows.map((e) => (
                        <div key={e.entry_ref} data-testid={`week-entry-${e.entry_ref}`}
                             className="rounded border border-border bg-muted/40 p-1.5">
                          <div className="text-[10.5px] font-medium leading-tight text-foreground">
                            {e.hours}h · {e.person_name}
                          </div>
                          <div className="truncate text-[10px] text-muted-foreground">
                            {e.reference_label}
                          </div>
                          <Chip className={`mt-1 ${STATUS_STYLE[e.status]}`}>{e.status}</Chip>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="queue" className="mt-4 space-y-3">
          {canApprove && queue.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" data-testid="approve-selected"
                      disabled={!picked.length || busy === "batch"}
                      onClick={() => act("batch",
                        () => api.post("/time/entries/approve-batch", { entry_refs: picked }),
                        `${picked.length} entries approved`)}
                      className="h-8 rounded-full bg-primary px-3 text-[11px] text-primary-foreground hover:bg-primary/90">
                {busy === "batch" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  : <CheckCheck className="mr-1 h-3 w-3" />}
                Approve {picked.length || "selected"}
              </Button>
              <Button size="sm" variant="outline" data-testid="select-all-queue"
                      onClick={() => setPicked(picked.length === queue.length ? []
                        : queue.map((e) => e.entry_ref))}
                      className="h-8 rounded-full border-border px-3 text-[10.5px]">
                {picked.length === queue.length ? "Clear selection" : "Select all"}
              </Button>
            </div>
          )}
          <Panel testId="panel-review-queue" kicker="Coordinator" title="Hours waiting on approval">
            <Table testId="review-table"
                   head={<><Th /><Th>Reference</Th><Th>Date</Th><Th>Person</Th><Th>Against</Th>
                     <Th>Hours</Th><Th>Logged by</Th><Th /></>}>
              {queue.length === 0 && <Empty colSpan={8} label="Nothing is waiting on approval" />}
              {queue.map((e) => (
                <tr key={e.entry_ref} className="row-hover" data-testid={`queue-row-${e.entry_ref}`}>
                  <Td>
                    {canApprove && (
                      <Checkbox checked={picked.includes(e.entry_ref)}
                                data-testid={`queue-pick-${e.entry_ref}`}
                                onCheckedChange={(v) => setPicked(v
                                  ? [...picked, e.entry_ref]
                                  : picked.filter((r) => r !== e.entry_ref))} />
                    )}
                  </Td>
                  <Td className="font-mono text-[10.5px]">{e.entry_ref}</Td>
                  <Td className="font-mono text-[10.5px]">{shortDate(e.date)}</Td>
                  <Td className="text-[11.5px] text-foreground">{e.person_name}
                    <div className="text-[10px] text-muted-foreground">{e.person_kind}</div></Td>
                  <Td className="text-[11.5px]">{e.reference_label}
                    {e.note && <div className="text-[10px] text-muted-foreground">{e.note}</div>}</Td>
                  <Td className="font-mono text-[11px]">{e.hours}</Td>
                  <Td className="font-mono text-[10px]">{e.logged_by}</Td>
                  <Td className="space-x-1.5 whitespace-nowrap">
                    {canApprove && (
                      <>
                        <Button size="sm" variant="outline" data-testid={`approve-${e.entry_ref}`}
                                disabled={busy === e.entry_ref}
                                onClick={() => act(e.entry_ref,
                                  () => api.post(`/time/entries/${e.entry_ref}/approve`, {}),
                                  `${e.hours} hours approved for ${e.person_name}`)}
                                className="h-7 rounded-full border-border px-2.5 text-[10px]">
                          Approve</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-${e.entry_ref}`}
                                onClick={() => { setRejecting(e); setReason(""); }}
                                className="h-7 rounded-full border-border px-2.5 text-[10px] text-red-600 dark:text-red-400">
                          Reject</Button>
                      </>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <Panel testId="panel-all-entries" kicker="Everything logged" title="Time entries">
            <Table testId="entries-table"
                   head={<><Th>Reference</Th><Th>Date</Th><Th>Person</Th><Th>Activity</Th>
                     <Th>Against</Th><Th>Hours</Th><Th>Status</Th><Th>Decided by</Th></>}>
              {data.items.length === 0 && <Empty colSpan={8} label="No time logged in this window" />}
              {data.items.map((e) => (
                <tr key={e.entry_ref} className="row-hover" data-testid={`entry-row-${e.entry_ref}`}>
                  <Td className="font-mono text-[10.5px]">{e.entry_ref}</Td>
                  <Td className="font-mono text-[10.5px]">{shortDate(e.date)}</Td>
                  <Td className="text-[11.5px] text-foreground">{e.person_name}</Td>
                  <Td className="text-[11px]">{e.activity}</Td>
                  <Td className="text-[11.5px]">{e.reference_label}</Td>
                  <Td className="font-mono text-[11px]">{e.hours}</Td>
                  <Td><Chip testId={`entry-status-${e.entry_ref}`} className={STATUS_STYLE[e.status]}>
                    {e.status}</Chip>
                    {e.rejected_reason && (
                      <div className="text-[10px] text-muted-foreground">{e.rejected_reason}</div>
                    )}</Td>
                  <Td className="font-mono text-[10px]">{e.approved_by || "—"}</Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel testId="panel-by-person" kicker="Approved hours" title="By person">
              <Table testId="by-person-table"
                     head={<><Th>Person</Th><Th>Hours</Th><Th>Entries</Th></>}>
                {summary.by_person.length === 0 && <Empty colSpan={3} label="No approved hours yet" />}
                {summary.by_person.map((r) => (
                  <tr key={r.key} className="row-hover" data-testid={`person-hours-${r.key}`}>
                    <Td className="text-[11.5px] text-foreground">{r.label}</Td>
                    <Td className="font-mono text-[11px]">{r.hours}</Td>
                    <Td className="font-mono text-[11px]">{r.entries}</Td>
                  </tr>
                ))}
              </Table>
            </Panel>
            <Panel testId="panel-by-programme" kicker="Approved hours" title="By programme and activity">
              <Table testId="by-programme-table"
                     head={<><Th>Programme / activity</Th><Th>Hours</Th><Th>People</Th></>}>
                {[...summary.by_programme, ...summary.by_activity].length === 0
                  && <Empty colSpan={3} label="No approved hours yet" />}
                {summary.by_programme.map((r) => (
                  <tr key={`p-${r.key}`} data-testid={`programme-hours-${r.key}`}>
                    <Td className="text-[11.5px] text-foreground">{r.label}</Td>
                    <Td className="font-mono text-[11px]">{r.hours}</Td>
                    <Td className="font-mono text-[11px]">{r.people}</Td>
                  </tr>
                ))}
                {summary.by_activity.map((r) => (
                  <tr key={`a-${r.key}`} data-testid={`activity-hours-${r.key}`}>
                    <Td className="text-[11px] text-muted-foreground">{r.label}</Td>
                    <Td className="font-mono text-[11px]">{r.hours}</Td>
                    <Td className="font-mono text-[11px]">{r.people}</Td>
                  </tr>
                ))}
              </Table>
            </Panel>
          </div>
          <Panel testId="panel-week-trend" kicker="Trend" title="Approved hours by week">
            <div className="h-[220px] px-3 py-3" data-testid="week-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.by_week}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={28} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", fontSize: 11,
                    border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
                  <Bar dataKey="hours" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </TabsContent>
      </Tabs>

      <LogTimeDialog open={logging} onOpenChange={setLogging} onLogged={load}
                     title={`Log time · ${user?.name || "team"}`} />

      <Dialog open={Boolean(rejecting)} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="max-w-md border-border bg-card" data-testid="reject-time-dialog">
          <DialogHeader><DialogTitle>Not approving {rejecting?.hours} hours</DialogTitle></DialogHeader>
          <Input value={reason} data-testid="reject-reason-input"
                 onChange={(e) => setReason(e.target.value)}
                 placeholder="Why? (at least 10 characters)"
                 className="bg-muted/40 text-[12px]" />
          <Button data-testid="reject-confirm" disabled={reason.trim().length < 10 || busy === "rej"}
                  onClick={async () => {
                    const ok = await act("rej",
                      () => api.post(`/time/entries/${rejecting.entry_ref}/reject`, { note: reason }),
                      "Entry rejected");
                    if (ok) setRejecting(null);
                  }}
                  className="h-9 rounded-full bg-red-600 text-white hover:bg-red-600/90">
            Record the decision
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
